import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../../identity/types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { questionFieldLabel } from '../../employee-onboarding/questions/questionFields';
import { peopleGateway } from '../index';
import { EMPLOYEE_PROFILE_STEPS, type DepartmentOption, type EmployeeResponse, type JobTitleOption } from '../types';

/** The questioned fields the Employees API can already change from here. Everything else is edited on the employee's profile. */
const EDITABLE = ['employeeNumber', 'hireDate', 'jobTitle', 'department'] as const;
type EditableKey = (typeof EDITABLE)[number];
const isEditable = (key: string): key is EditableKey => (EDITABLE as readonly string[]).includes(key);

const DUPLICATE_NUMBER = /employee number is already in use/i;

/**
 * EPN-216: the field an employee asked about, shown right under their question so the administrator can correct it without leaving the
 * page. It uses the existing employee update, so every rule (and the audit trail) of the profile still applies. Fields the update does
 * not cover link to the employee's profile instead, and the SSN is never editable here (the employee enters it).
 */
export function QuestionFieldEditor({ employeeId, fieldKey }: { employeeId: string; fieldKey: string }) {
  const uid = useId();
  const label = questionFieldLabel(fieldKey);
  const [employee, setEmployee] = useState<EmployeeResponse | null>(null);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [jobTitles, setJobTitles] = useState<JobTitleOption[]>([]);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const editable = isEditable(fieldKey);

  useEffect(() => {
    if (!editable) return;
    let cancelled = false;
    void Promise.all([peopleGateway.getEmployee(employeeId), peopleGateway.listDepartments(), peopleGateway.listJobTitles()])
      .then(([e, d, j]) => {
        if (cancelled) return;
        setEmployee(e);
        setDepartments(d.filter((x) => x.isActive));
        setJobTitles(j.filter((x) => x.isActive));
        setValue(initial(fieldKey, e));
      })
      .catch(() => { if (!cancelled) setLoadFailed(true); });
    return () => { cancelled = true; };
  }, [editable, employeeId, fieldKey]);

  const profileLink = (
    <Link to={`/people/${employeeId}/${EMPLOYEE_PROFILE_STEPS[0].key}`}>Open the employee&rsquo;s profile</Link>
  );

  if (fieldKey === 'ssn') {
    return <p className="question-field__note">The SSN is entered by the employee and cannot be changed here. Ask them to re-enter it in their setup.</p>;
  }
  if (!editable) {
    return <p className="question-field__note">{label} is edited in the employee&rsquo;s profile. {profileLink}.</p>;
  }
  if (loadFailed) return <p className="question-field__note" role="alert">Could not load {label}. {profileLink}.</p>;
  if (!employee) return <p className="question-field__note" role="status">Loading {label}…</p>;

  async function save() {
    if (!employee) return;
    setError(null);
    setSaved(false);
    if (!value) { setError(`Enter the ${label}.`); return; }
    setBusy(true);
    try {
      const next = await peopleGateway.updateEmployee(employee.id, {
        firstName: employee.firstName,
        lastName: employee.lastName,
        email: employee.email,
        dateOfBirth: employee.dateOfBirth,
        ssnLast4: employee.ssnLast4,
        phone: employee.phone ?? null,
        gender: employee.gender ?? null,
        isNewHire: employee.isNewHire ?? null,
        employmentType: employee.employmentType ?? null,
        departmentId: fieldKey === 'department' ? value : employee.departmentId,
        jobTitleId: fieldKey === 'jobTitle' ? value : employee.jobTitleId,
        hireDate: fieldKey === 'hireDate' ? value : employee.hireDate,
        employeeNumber: fieldKey === 'employeeNumber' ? value.trim() : employee.employeeNumber,
      });
      setEmployee(next);
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError && DUPLICATE_NUMBER.test(err.message)
        ? 'That Employee ID is already in use in this company. Enter a different one.'
        : friendlyErrorMessage(err, `Could not save the ${label}.`));
    } finally {
      setBusy(false);
    }
  }

  const id = `${uid}-field`;
  const change = (v: string) => { setValue(v); setSaved(false); setError(null); };
  return (
    <div className="question-field">
      <label htmlFor={id}>{label} (editable here)</label>
      {fieldKey === 'employeeNumber' && <input id={id} value={value} disabled={busy} onChange={(e) => change(e.target.value)} autoComplete="off" />}
      {fieldKey === 'hireDate' && <input id={id} type="date" value={value} disabled={busy} onChange={(e) => change(e.target.value)} />}
      {fieldKey === 'department' && (
        <select id={id} value={value} disabled={busy} onChange={(e) => change(e.target.value)}>
          <option value="">Select</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      )}
      {fieldKey === 'jobTitle' && (
        <select id={id} value={value} disabled={busy} onChange={(e) => change(e.target.value)}>
          <option value="">Select</option>
          {jobTitles.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
        </select>
      )}
      {error && <p className="error" role="alert">{error}</p>}
      {saved && <p className="question-field__saved" role="status">{label} updated. Now let the employee know in your reply.</p>}
      <button type="button" className="employee-list__add" onClick={() => void save()} disabled={busy || value === initial(fieldKey, employee)}>
        {busy ? 'Saving…' : `Save ${label}`}
      </button>
    </div>
  );
}

function initial(key: string, e: EmployeeResponse): string {
  switch (key) {
    case 'employeeNumber': return e.employeeNumber;
    case 'hireDate': return e.hireDate.slice(0, 10);
    case 'jobTitle': return e.jobTitleId ?? '';
    case 'department': return e.departmentId ?? '';
    default: return '';
  }
}
